import { useState, forwardRef, FocusEvent, ChangeEvent } from "react";
import cn from "classnames";
import { Icon } from "../input/components/icon";
import { Text } from "../text";
import { TextareaProps } from "./types";
import styles from "./styles.module.scss";

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      id,
      name,
      icon,
      hint,
      value,
      label,
      placeholder,
      error,
      required,
      disabled,
      className,
      maxLength,
      autofocus = false,
      dataTestId = "textarea",
      onChange,
      onFocus,
      onBlur,
      onIconBlur,
      onIconFocus,
      onIconPress,
      onIconMouseEnter,
      onIconMouseLeave,
    },
    forwardedRef
  ) => {
    const [focused, setFocus] = useState(false);

    const handleFocus = (event: FocusEvent<HTMLTextAreaElement>) => {
      setFocus(true);
      onFocus?.(event);
    };

    const handleBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
      setFocus(false);
      onBlur?.(event);
    };

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(event);
    };

    const isFloated = value || focused;

    return (
      <div
        className={cn(
          styles.container,
          { [styles.error]: error },
          { [styles.focused]: focused },
          { [styles.disabled]: disabled },
          className
        )}
        data-test-id={`${dataTestId}-container`}
      >
        {label && (
          <Text
            lineHeight="short"
            size={isFloated ? "xsmall" : "medium"}
            dataTestId={`${dataTestId}-label`}
            className={cn(styles.label, {
              [styles.floated]: isFloated,
              [styles.disabled]: disabled,
            })}
          >
            {label}
          </Text>
        )}
        {icon && (
          <Icon
            icon={icon}
            className={styles.icon}
            inputDisabled={disabled}
            onIconBlur={onIconBlur}
            onIconPress={onIconPress}
            onIconFocus={onIconFocus}
            onIconMouseEnter={onIconMouseEnter}
            onIconMouseLeave={onIconMouseLeave}
            dataTestId={`${dataTestId}-icon`}
          />
        )}
        <textarea
          id={id}
          placeholder={label ? undefined : placeholder}
          name={name}
          value={value}
          ref={forwardedRef}
          disabled={disabled}
          required={required}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autofocus}
          maxLength={maxLength}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={handleChange}
          data-test-id={dataTestId}
          className={cn(styles.textarea, { [styles["with-label"]]: label })}
        />
        {hint && (
          <Text
            size="xsmall"
            dataTestId={`${dataTestId}-hint`}
            className={cn(styles.hint, {
              [styles.error]: error,
              [styles.disabled]: disabled,
            })}
          >
            {hint}
          </Text>
        )}
      </div>
    );
  }
);
